import {
  type PaymentHistory,
  type PaymentHistoryRepository,
} from "../interfaces";
import { NextPaymentService } from "./services/NextPaymentService";
import { SubscriptionQueryUtils } from "./utils/SubscriptionQueryUtils";
import { supabase } from "@/integrations/supabase/supabaseClient";

export class SupabasePaymentHistoryRepository
  implements PaymentHistoryRepository
{
  async getAll(): Promise<PaymentHistory[]> {
    const subscriptionIds =
      await SubscriptionQueryUtils.getUserSubscriptionIds();

    if (subscriptionIds.length === 0) {
      return [];
    }

    const { data, error } = await supabase
      .from("payment_history")
      .select(
        `
        id,
        subscription_id,
        payment_date,
        amount,
        currency,
        status,
        reconciliation_source,
        notes,
        created_at
      `,
      )
      .in("subscription_id", subscriptionIds);

    if (error) throw error;
    return data as (PaymentHistory & {
      status: "paid" | "cancelled" | "missed" | "refunded";
      reconciliation_source: "manual" | "imported" | "ynab" | "bank" | "other";
    })[];
  }

  async getBySubscriptionId(subscriptionId: string): Promise<PaymentHistory[]> {
    const { data, error } = await supabase
      .from("payment_history")
      .select("*")
      .eq("subscription_id", subscriptionId)
      .order("payment_date", { ascending: false });

    if (error) throw error;
    return data as (PaymentHistory & {
      status: "paid" | "cancelled" | "missed" | "refunded";
      reconciliation_source: "manual" | "imported" | "ynab" | "bank" | "other";
    })[];
  }

  async create(
    payment: Omit<PaymentHistory, "id" | "created_at">,
  ): Promise<PaymentHistory> {
    const { data, error } = await supabase
      .from("payment_history")
      .insert(payment)
      .select()
      .single();

    if (error) throw error;

    // If this was a successful payment, recalc and persist next_payment_date on the subscription
    if (data && data.status === "paid") {
      await NextPaymentService.recalculateAndUpdateNextPayment(
        data.subscription_id,
      );
    }

    return data as PaymentHistory & {
      status: "paid" | "cancelled" | "missed" | "refunded";
      reconciliation_source: "manual" | "imported" | "ynab" | "bank" | "other";
    };
  }

  async update(
    id: string,
    updates: Partial<PaymentHistory>,
  ): Promise<PaymentHistory> {
    const { data, error } = await supabase
      .from("payment_history")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;

    // If this update affects status/date, recalc subscription next_payment_date based on latest 'paid'
    if (
      data &&
      (data.status === "paid" ||
        "status" in updates ||
        "payment_date" in updates)
    ) {
      await NextPaymentService.recalculateAndUpdateNextPayment(
        data.subscription_id,
      );
    }

    return data as PaymentHistory & {
      status: "paid" | "cancelled" | "missed" | "refunded";
      reconciliation_source: "manual" | "imported" | "ynab" | "bank" | "other";
    };
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from("payment_history")
      .delete()
      .eq("id", id);

    if (error) throw error;
  }

  async bulkCreate(
    payments: Omit<PaymentHistory, "id" | "created_at">[],
  ): Promise<PaymentHistory[]> {
    const { data, error } = await supabase
      .from("payment_history")
      .insert(payments)
      .select();

    if (error) throw error;

    if (Array.isArray(data)) {
      await NextPaymentService.updateNextPaymentsForPaidPayments(data);
    }

    return data as (PaymentHistory & {
      status: "paid" | "cancelled" | "missed" | "refunded";
      reconciliation_source: "manual" | "imported" | "ynab" | "bank" | "other";
    })[];
  }

  async deleteAll(): Promise<void> {
    const subscriptionIds =
      await SubscriptionQueryUtils.getUserSubscriptionIds();

    if (subscriptionIds.length === 0) {
      return;
    }

    // Delete payment history for user's subscriptions
    const { error } = await supabase
      .from("payment_history")
      .delete()
      .in("subscription_id", subscriptionIds);

    if (error) throw error;
  }
}
